import { createSelector } from '@reduxjs/toolkit';
import { get, kebabCase } from 'lodash';
import { Store } from 'components/common/types/Store.types';
import { Asset } from 'components/common/types/Asset.types';
import { distributionGroupPathToValue } from 'components/pages/Planner/AvailabilityDistribution/consts';

const filterAssetsByDistribution = (assets: Asset[], selectedItems: string[], groupBy: string): Asset[] => {
  if (!selectedItems?.length) return assets;

  const labelPath = distributionGroupPathToValue[groupBy]?.label;
  if (!labelPath) return assets;

  const selectedSet = new Set(selectedItems);

  return assets.filter((asset: Asset) => {
    const groupId = kebabCase(get(asset, labelPath));
    return selectedSet.has(groupId);
  });
};

export const getCurrentLineAvailability = createSelector(
  [(state: Store) => state.dealManagement.commonDeal.currentLine],
  (line) => line.availability,
);

export const getUniverseImpressions = createSelector(
  [getCurrentLineAvailability],
  (availability) =>
    availability.universeImpressions ??
    (availability.assets as Asset[]).reduce((total, asset) => total + (asset.totalImpressions ?? 0), 0),
);

export const getAvailableAssets = createSelector([getCurrentLineAvailability], (availability) => availability.assets);

export const getUnavailableAssets = createSelector(
  [getCurrentLineAvailability],
  (availability) => availability.unavailableAssets ?? [],
);

export const getMissingAssets = createSelector(
  [getCurrentLineAvailability],
  (availability) => availability.missingAssets ?? [],
);

export const getAvailableFrames = createSelector(
  [getCurrentLineAvailability],
  (availability) => availability.availableFrames,
);

export const getAvailableImpressions = createSelector(
  [getCurrentLineAvailability],
  (availability) => availability.availableImpressions,
);

export const getAllocatedFrames = createSelector(
  [getCurrentLineAvailability],
  (availability) => availability.allocatedFrames,
);

export const getAllocatedImpressions = createSelector(
  [getCurrentLineAvailability],
  (availability) => availability.allocatedImpressions,
);

export const getNumberOfUnavailableAssets = createSelector(
  [getUnavailableAssets],
  (unavailableAssets) => unavailableAssets.length,
);

export const getAssetsDictionary = createSelector(
  [getAvailableAssets, getUnavailableAssets, getMissingAssets],
  (availableAssets, unavailableAssets, missingAssets) =>
    new Map([...availableAssets, ...unavailableAssets, ...missingAssets].map((asset) => [asset.frameId, asset])),
);

export const getFilteredAssets = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.currentLine.availability.assets,
    (state: Store) => state.dealManagement.plannerSettings.filteredAssetsIds,
  ],
  (assets, filteredAssetsIds) =>
    assets.filter((asset: Asset) => {
      const compositeKey = `${asset.mediaOwner}:${asset.routeFrameCode}`;
      return filteredAssetsIds[compositeKey];
    }) as Asset[],
);

export const getSelectedDistributionAssets = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.currentLine.availability.assets,
    (state: Store) => state.dealManagement.commonDeal.currentLine.distributionOfAvailabilityFilter.selectedItems,
    (state: Store) => state.dealManagement.commonDeal.currentLine.distributionOfAvailabilityFilter.groupBy,
  ],
  (assets, selectedItems, groupBy) => filterAssetsByDistribution(assets as Asset[], selectedItems, groupBy),
);

export const getFilteredAssetsSummary = createSelector(
  [
    getFilteredAssets,
    getSelectedDistributionAssets,
    (state: Store) => state.dealManagement.plannerSettings.isClientSideAssetFiltering,
  ],
  (filteredAssets, distributionFilteredAssets, isClientSideAssetFiltering) => {
    const includedAssets = (isClientSideAssetFiltering ? filteredAssets : distributionFilteredAssets) as Asset[];
    const includedFrames = includedAssets.filter(
      ({ availability, impressions }) => availability === 'Available' || impressions > 0,
    ).length;
    const includedImpressions = includedAssets.reduce((total, curr) => total + (curr.impressions ?? 0), 0);

    return {
      filteredAssets: includedAssets,
      filteredFrames: includedFrames,
      filteredImpressions: includedImpressions,
    };
  },
);
